
import { supabase } from "@/integrations/supabase/client";
import { Profile } from "./types";
import { updateProfileCache } from "./profileCache";
import { createLogger } from "../utils/loggingUtils";
import { toast } from "sonner";

const logger = createLogger('updateProfile');

export const updateProfile = async (userId: string, updates: Partial<Profile>): Promise<Profile | null> => {
  try {
    logger.info("Updating profile for user:", userId, "with updates:", updates);
    
    const { data, error } = await supabase
      .from("profiles")
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq("id", userId)
      .select()
      .single();

    if (error) {
      logger.error("Error updating profile:", error);
      
      // If auth error, try refreshing session once
      if (error.message.includes('JWT') || error.code === 'PGRST301') {
        logger.warn("JWT error updating profile, attempting to refresh session");
        
        try {
          await supabase.auth.refreshSession();
          
          // Retry after session refresh
          const { data: retryData, error: retryError } = await supabase
            .from("profiles")
            .update({
              ...updates,
              updated_at: new Date().toISOString()
            })
            .eq("id", userId)
            .select()
            .single();
            
          if (retryError) {
            logger.error("Error updating profile after session refresh:", retryError);
            toast.error("Failed to update profile");
            return null;
          }
          
          // Update the cache with the updated profile
          updateProfileCache(userId, retryData as Profile);
          logger.info("Profile updated successfully after session refresh:", retryData);
          toast.success("Profile updated successfully");
          return retryData as Profile;
        } catch (refreshError) {
          logger.error("Error refreshing session:", refreshError);
          toast.error("Failed to update profile");
          return null;
        }
      }
      
      toast.error("Failed to update profile");
      return null;
    }

    // Update the cache with the updated profile
    updateProfileCache(userId, data as Profile);
    
    logger.info("Profile updated successfully:", data);
    toast.success("Profile updated successfully");
    return data as Profile;
  } catch (error) {
    logger.error("Exception updating profile:", error);
    toast.error("An unexpected error occurred");
    return null;
  }
};
